import { COMENSAL_DEVICE_TOKEN_KEY } from './sessionStorageKeys';

const KEY = 'olulo_pending_bill_receipt_v1';
const SESSION_CHARGE_RESULT_KEY = 'olulo_session_charge_result_v1';
const SESSION_CHARGE_RESULT_TTL_MS = 30 * 60 * 1000;
export const PENDING_BILL_RECEIPT_CHANGED_EVENT = 'olulo:pending-bill-receipt-changed';

export interface PendingBillReceiptContext {
  sessionToken: string;
  billId: number;
  deviceToken: string;
}

export interface SessionChargeResultContext {
  sessionToken: string;
  bundleId: number;
  billId?: number;
  deviceToken: string;
  createdAt: number;
}

export function persistSessionChargeResult(
  sessionToken: string,
  bundleId: number,
  deviceToken: string,
  billId?: number,
): void {
  if (
    typeof window === 'undefined'
    || sessionToken === ''
    || deviceToken === ''
    || !Number.isInteger(bundleId)
    || bundleId < 1
    || (billId !== undefined && (!Number.isInteger(billId) || billId < 1))
  ) return;
  const context: SessionChargeResultContext = {
    sessionToken,
    bundleId,
    deviceToken,
    createdAt: Date.now(),
    ...(billId === undefined ? {} : { billId }),
  };
  window.sessionStorage.setItem(SESSION_CHARGE_RESULT_KEY, JSON.stringify(context));
}

export function readSessionChargeResult(): SessionChargeResultContext | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(SESSION_CHARGE_RESULT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SessionChargeResultContext>;
    const currentSession = window.sessionStorage.getItem('table_session_token');
    const currentDevice = window.sessionStorage.getItem(COMENSAL_DEVICE_TOKEN_KEY);
    if (
      typeof parsed.sessionToken !== 'string'
      || parsed.sessionToken === ''
      || !Number.isInteger(parsed.bundleId)
      || (parsed.bundleId ?? 0) < 1
      || typeof parsed.deviceToken !== 'string'
      || parsed.deviceToken === ''
      || typeof parsed.createdAt !== 'number'
      || parsed.createdAt > Date.now()
      || Date.now() - parsed.createdAt > SESSION_CHARGE_RESULT_TTL_MS
      || (parsed.billId !== undefined
        && (!Number.isInteger(parsed.billId) || parsed.billId < 1))
      || (currentSession !== null && currentSession !== parsed.sessionToken)
      || (currentDevice !== null && currentDevice !== parsed.deviceToken)
    ) {
      window.sessionStorage.removeItem(SESSION_CHARGE_RESULT_KEY);
      return null;
    }
    return parsed as SessionChargeResultContext;
  } catch {
    window.sessionStorage.removeItem(SESSION_CHARGE_RESULT_KEY);
    return null;
  }
}

export function clearSessionChargeResult(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(SESSION_CHARGE_RESULT_KEY);
}

export function persistPendingBillReceipt(sessionToken: string, billId: number): void {
  if (typeof window === 'undefined' || !Number.isInteger(billId) || billId < 1) return;
  const deviceToken = window.sessionStorage.getItem(COMENSAL_DEVICE_TOKEN_KEY);
  if (!deviceToken) return;
  window.sessionStorage.setItem(KEY, JSON.stringify({ sessionToken, billId, deviceToken }));
  window.dispatchEvent(new Event(PENDING_BILL_RECEIPT_CHANGED_EVENT));
}

export function readPendingBillReceipt(): PendingBillReceiptContext | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingBillReceiptContext>;
    const valid = typeof parsed.sessionToken === 'string'
      && parsed.sessionToken !== ''
      && Number.isInteger(parsed.billId)
      && (parsed.billId ?? 0) > 0
      && typeof parsed.deviceToken === 'string'
      && parsed.deviceToken !== ''
      && window.sessionStorage.getItem('table_session_token') === parsed.sessionToken
      && window.sessionStorage.getItem(COMENSAL_DEVICE_TOKEN_KEY) === parsed.deviceToken;
    if (!valid) {
      window.sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed as PendingBillReceiptContext;
  } catch {
    window.sessionStorage.removeItem(KEY);
    return null;
  }
}

export function clearPendingBillReceipt(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(KEY);
  window.dispatchEvent(new Event(PENDING_BILL_RECEIPT_CHANGED_EVENT));
}

export function clearPendingBillReceiptIfMatches(input: {
  sessionToken: string;
  billId: number;
  deviceToken: string;
}): void {
  if (typeof window === 'undefined') return;
  const raw = window.sessionStorage.getItem(KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingBillReceiptContext>;
    if (
      parsed.sessionToken !== input.sessionToken
      || parsed.billId !== input.billId
      || parsed.deviceToken !== input.deviceToken
    ) return;
  } catch {
    return;
  }
  clearPendingBillReceipt();
}

/** sessionStorage는 같은 탭에서 storage 이벤트가 없어 명시 이벤트로 React 소비자를 깨운다. */
export function subscribePendingBillReceipt(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(PENDING_BILL_RECEIPT_CHANGED_EVENT, listener);
  return () => { window.removeEventListener(PENDING_BILL_RECEIPT_CHANGED_EVENT, listener); };
}
